import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@devhop/ui";
import { CheckIcon } from "lucide-react";
import type { EventProgressDTO } from "../../domain/contracts";
import { formatPayoutStatusLabel } from "../lib/payout-status-ui";

const MILESTONE_LABELS: Record<string, string> = {
  team: "ຈັດທີມ",
  pricing: "ລາຄາ",
  rehearsal: "ວັນຊ້ອມ",
  payout: "ເບີກຈ່າຍ",
  invoiceIssued: "ໃບເກັບເງິນ",
  paymentCollected: "ຮັບເງິນຄົບ",
};

type MilestoneEntry = {
  key: string;
  label: string;
  done: boolean;
};

function milestoneStatusLabel(
  item: MilestoneEntry,
  progress: EventProgressDTO,
): string {
  if (item.key === "payout") {
    const statusText = formatPayoutStatusLabel(progress.payoutStatus);
    if (progress.apPercent != null && progress.payoutStatus !== "paid") {
      return `${statusText} · ${progress.apPercent}%`;
    }
    return statusText;
  }
  return item.done ? "ສຳເລັດ" : "ລໍຖ້າ";
}

function getMilestoneEntries(
  progress: EventProgressDTO,
): MilestoneEntry[] {
  const m = progress.milestones;
  const entries: Array<[string, boolean | null | undefined]> = [
    ["team", m.team],
    ["pricing", m.pricing],
    ["rehearsal", m.rehearsal],
    ["payout", m.payout],
    ["invoiceIssued", m.invoiceIssued],
    ["paymentCollected", m.paymentCollected],
  ];

  return entries
    .filter(([, value]) => value != null)
    .map(([key, value]) => ({
      key,
      label: MILESTONE_LABELS[key] ?? key,
      done: !!value,
    }));
}

function ProgressPopoverBody({ progress }: { progress: EventProgressDTO }) {
  const cancelled = progress.operationalPercent == null;
  const milestones = cancelled ? [] : getMilestoneEntries(progress);
  const doneCount = milestones.filter((m) => m.done).length;

  return (
    <div className="space-y-3">
      <div>
        <p className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
          ຄວາມຄືບໜ້າປະຕິບັດການ
        </p>
        {cancelled ? (
          <p className="mt-1 font-semibold text-sm">ງານຖືກຍົກເລີກ</p>
        ) : (
          <p className="mt-1 font-bold text-2xl tabular-nums">
            {progress.operationalPercent}%
            <span className="ml-2 font-normal text-muted-foreground text-xs">
              {doneCount}/{milestones.length} ຂັ້ນຕອນ
            </span>
          </p>
        )}
      </div>

      {!cancelled && milestones.length > 0 ? (
        <ul className="space-y-1.5 border-t pt-2">
          {milestones.map((item) => (
            <li key={item.key} className="flex items-center gap-2 text-sm">
              <span
                className={
                  item.done
                    ? "flex size-4 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
                    : "flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50"
                }
              >
                {item.done ? (
                  <CheckIcon className="size-2.5" aria-hidden />
                ) : null}
              </span>
              <span
                className={
                  item.done ? "text-foreground" : "text-muted-foreground"
                }
              >
                {item.label}
              </span>
              <span className="ml-auto text-muted-foreground text-xs">
                {milestoneStatusLabel(item, progress)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid grid-cols-2 gap-2 border-t pt-2">
        <div className="rounded-md bg-muted/40 px-2.5 py-2">
          <p className="text-muted-foreground text-xs">AR · ເກັບເງິນ</p>
          <p className="mt-0.5 font-semibold text-sm tabular-nums">
            {progress.arPercent == null ? "—" : `${progress.arPercent}%`}
          </p>
        </div>
        <div className="rounded-md bg-muted/40 px-2.5 py-2">
          <p className="text-muted-foreground text-xs">AP · ຈ່າຍນັກສະແດງ</p>
          <p className="mt-0.5 font-semibold text-sm tabular-nums">
            {progress.apPercent == null ? "—" : `${progress.apPercent}%`}
          </p>
          {progress.payoutStatus ? (
            <p className="mt-0.5 text-muted-foreground text-[11px]">
              {formatPayoutStatusLabel(progress.payoutStatus)}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

type EventProgressBarProps = {
  progress: EventProgressDTO | null | undefined;
  className?: string;
  showLabel?: boolean;
};

export function EventProgressBar({
  progress,
  className,
  showLabel = true,
}: EventProgressBarProps) {
  if (!progress) {
    return (
      <span className={`text-muted-foreground text-sm ${className ?? ""}`}>
        —
      </span>
    );
  }

  const cancelled = progress.operationalPercent == null;
  const pct = progress.operationalPercent;

  const trigger = (
    <button
      type="button"
      className={`min-w-28 max-w-40 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ""}`}
    >
      {cancelled ? (
        <span className="text-muted-foreground text-sm">—</span>
      ) : (
        <>
          <div className="mb-1 flex items-center justify-between gap-2">
            {showLabel ? (
              <span className="font-medium text-xs tabular-nums">{pct}%</span>
            ) : null}
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      )}
    </button>
  );

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent align="start" side="top" className="w-72 p-3">
        <ProgressPopoverBody progress={progress} />
      </HoverCardContent>
    </HoverCard>
  );
}
